import React, { useState, useRef, useEffect, useCallback } from "react";
import Pdf from "@mikecousins/react-pdf";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Container,
  IconButton,
  LinearProgress,
  Stack,
} from "@mui/material";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import VSNTypography from "../../shared/VSNTypography";
import VSNLoader from "../../shared/VSNLoader";

const PresentationViewer = ({ data, loader }) => {
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const viewerRef = useRef();

  const updateScale = useCallback(() => {
    const width = viewerRef.current?.offsetWidth || 1200;
    setScale(width / 1400);
  }, []);

  const handleFullscreenChange = () => {
    const isFull = !!document.fullscreenElement;
    setIsFullscreen(isFull);
    setTimeout(updateScale, 100);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      viewerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    updateScale();
    window.addEventListener("resize", updateScale);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      window.removeEventListener("resize", updateScale);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [updateScale]);

  useEffect(() => {
    if (data?.link) {
      setPage(1);
      setNumPages(null);
    }
  }, [data?.link]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" && page < numPages) setPage(page + 1);
      else if (e.key === "ArrowLeft" && page > 1) setPage(page - 1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [page, numPages]);

  if (loader) return <VSNLoader variant="transParent" />;

  return (
    <Container maxWidth="xl" p={{ sm: 4, xs: 2 }}>
      {data ? (
        <>
          <Box py={3}>
            <Box
              ref={viewerRef}
              sx={{
                position: "relative",
                width: isFullscreen ? "100%" : "100%",
                maxHeight: isFullscreen ? "100vh" : "700px",
                minHeight: isFullscreen ? "100vh" : "600px",
                margin: "auto",
                backgroundColor: "var(--gray)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              {data?.link ? (
                <Pdf
                  key={data.link}
                  file={data.link}
                  page={page}
                  scale={scale}
                  onLoadSuccess={({ numPages }) => {
                    setNumPages(numPages);
                    setPage(1);
                  }}
                >
                  {({ pdfDocument, canvas }) => (
                    <>
                      {!pdfDocument && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.6)",
                            zIndex: 10,
                          }}
                        >
                          <CircularProgress size={24} />
                        </Box>
                      )}
                      <Box
                        sx={{
                          width: "auto",
                          height: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          "& canvas": {
                            width: "100% !important",
                            height: "100% !important",
                            objectFit: "cover",
                          },
                        }}
                      >
                        {canvas}
                      </Box>

                      <IconButton
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: 10,
                          transform: "translateY(-50%)",
                          backgroundColor: "#FFFFFFB2",
                          borderRadius: "4px",
                        }}
                      >
                        <KeyboardArrowLeftOutlinedIcon />
                      </IconButton>

                      <IconButton
                        disabled={pdfDocument && page === pdfDocument.numPages}
                        onClick={() => setPage(page + 1)}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: 10,
                          transform: "translateY(-50%)",
                          backgroundColor: "#FFFFFFB2",
                          borderRadius: "4px",
                        }}
                      >
                        <ChevronRightOutlinedIcon />
                      </IconButton>

                      <LinearProgress
                        variant="determinate"
                        value={
                          pdfDocument?.numPages
                            ? (page / pdfDocument?.numPages) * 100
                            : 0
                        }
                        sx={{
                          position: "absolute",
                          bottom: 40,
                          left: 0,
                          right: 0,
                          width: "100%",
                          height: 6,
                          backgroundColor: "#EAEAEC",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "#FF743C",
                          },
                        }}
                      />

                      {pdfDocument && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 10,
                            left: 20,
                            color: "#fff",
                            zIndex: 111,
                          }}
                        >
                          Page {page} / {pdfDocument.numPages}
                        </Box>
                      )}

                      <Box
                        onClick={toggleFullscreen}
                        sx={{
                          position: "absolute",
                          bottom: 5,
                          right: 20,
                          color: "#fff",
                          cursor: "pointer",
                          zIndex: 111,
                        }}
                      >
                        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                      </Box>

                      {!isFullscreen && (
                        <Box
                          onClick={toggleFullscreen}
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            left: 0,
                            cursor: "pointer",
                            height: "40px",
                            backgroundColor: "#000",
                            width: "100%",
                            opacity: 0.5,
                            zIndex: 0,
                          }}
                        />
                      )}
                    </>
                  )}
                </Pdf>
              ) : (
                "No Content Found"
              )}
            </Box>
          </Box>

          <Stack spacing={1} my={{ sm: 4, xs: 2 }}>
            {/* <VSNTypography color="var(--secondary)" fontWeight={700}>
          Personal Development for a Sa
        </VSNTypography> */}

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <VSNTypography
                fontSize={{ md: 40, sm: 30, xs: 24 }}
                fontWeight={700}
              >
                {data?.title ?? ""}
              </VSNTypography>
              {/* <IconButton className={classes.addIcon}>
            <AddIcon fontSize="large" />
          </IconButton> */}
            </Stack>

            <VSNTypography
              fontSize={{ md: 24, sm: 20, xs: 18 }}
              fontWeight={400}
              color="text.secondary"
              dangerouslySetInnerHTML={{
                __html: data?.summary,
              }}
              pb={1}
            />

            <VSNTypography>
              Instructor(s):{" "}
              <VSNTypography sx={{ fontWeight: "600" }} component="strong">
                {data?.instructor ?? ""}
              </VSNTypography>
            </VSNTypography>

            <Stack
              direction="row"
              pt={2}
              gap={1}
              flexWrap="wrap"
              alignItems="center"
            >
              <VSNTypography mr={2} sx={{ flex: "none" }}>
                Topics Covered:
              </VSNTypography>
              <Stack direction="row" flexWrap="wrap" gap={1.5} flex={{ sm: 1 }}>
                {data?.tags?.map((tag, index) => (
                  <VSNTypography
                    key={index}
                    sx={{
                      padding: "8px",
                      backgroundColor: "#FEF6EE",
                      borderRadius: "8px",
                      fontWeight: 700,
                      color: "var(--secondary)",
                    }}
                  >
                    {tag}
                  </VSNTypography>
                ))}
              </Stack>

              {/* <VSNButton variant="outlined" sx={{ px: 2, flex: "none" }}>
            Mark As Complete
          </VSNButton> */}
            </Stack>
          </Stack>
        </>
      ) : (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          py={10}
        >
          No data found
        </Stack>
      )}
    </Container>
  );
};

export default PresentationViewer;
